import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import * as THREE from 'three';
import { IFile } from 'viewer3d';
import partsService from '../../../../services/api/partsService';
import { IDLE_PART_COLOR, SUPPORT_PART_COLOR, TRAILER_BOX_NAME } from '../editMode.constants';
import { toMatrix4 } from '../editMode.utils';
import { getBasePartId, isDunnagePartId, isPalletPartId, isSupportPartId, sortNestedParts } from '../partialRenest.utils';

export interface LoadedPart {
  part_id: string;
  order_number: number;
  transform_matrix: number[][];
  part_index?: number;
  orientation_index?: number;
  child_items?: LoadedPart[];
  isChild?: boolean;
}

export interface LayerData {
  supportObjects: LoadedPart[];
  parts: LoadedPart[];
}

export interface SasUrlMapping {
  [key: string]: string;
}

const SCENE_GENERATION_KEY = 'sceneGeneration';

export const getIdlePartColor = (partId: string) => (isSupportPartId(partId) ? SUPPORT_PART_COLOR : IDLE_PART_COLOR);

export const setPalletChildrenVisibility = (
  palletId: string,
  visible: boolean,
  scenePartsMap: { [partId: string]: any },
  palletChildrenMap: { [palletId: string]: string[] }
) => {
  const children = palletChildrenMap[palletId];
  if (!children) return;
  children.forEach((childUuid) => {
    const childObj = scenePartsMap[childUuid];
    if (childObj) {
      childObj.visible = visible;
      childObj.updateMatrixWorld(true);
    }
  });
};

interface UsePartLoadingParams {
  viewer: any;
  currentLoadingPlan: any;
  getTransformControls: () => any;
  currentPartRef: React.MutableRefObject<THREE.Object3D | null>;
  hasInitializedVisibilityRef: React.MutableRefObject<boolean>;
  replacementAcceptedRef: React.MutableRefObject<boolean>;
  isRenestInFlightRef: React.MutableRefObject<boolean>;
  restoreToPartIdRef: React.MutableRefObject<string | null>;
  setActivePartId: (partId: string | null) => void;
  setCurrentStep: (step: number) => void;
  setCurrentLayer: (layer: number) => void;
  setTotalSteps: (steps: number) => void;
}

// Owns scene part loading: fetching SAS URLs, loading STL models, and rebuilding the scene
// when the loading plan's nesting output changes.
export function usePartLoading({
  viewer,
  currentLoadingPlan,
  getTransformControls,
  currentPartRef,
  hasInitializedVisibilityRef,
  replacementAcceptedRef,
  isRenestInFlightRef,
  restoreToPartIdRef,
  setActivePartId,
  setCurrentStep,
  setCurrentLayer,
  setTotalSteps,
}: UsePartLoadingParams) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [layerData, setLayerData] = useState<{ [key: number]: LayerData }>({});
  const [sasUrlMapping, setSasUrlMapping] = useState<SasUrlMapping>({});
  const [isLoadingParts, setIsLoadingParts] = useState(true);
  const [scenePartsMap, setScenePartsMap] = useState<{ [partId: string]: any }>({});
  const [palletChildrenMap, setPalletChildrenMap] = useState<{ [palletId: string]: string[] }>({});
  const hasLoadedPartsRef = useRef(false);
  // Bumped by every scene reset. Loads are async (fetch + STL parse), so a load that started for an
  // older nesting output must not add its object to the freshly rebuilt scene.
  const sceneGenerationRef = useRef(0);

  const loadSinglePart = useCallback(async (part: any, generation: number = sceneGenerationRef.current) => {
    if (!viewer || !currentLoadingPlan?.id) return;

    const scene = viewer.getScene();
    const basePartId = getBasePartId(part.part_id);
    const isStale = () => generation !== sceneGenerationRef.current;

    try {
      const isDunnage = isDunnagePartId(basePartId);
      const isPallet = isPalletPartId(basePartId);

      const sasUrl =
        isDunnage || isPallet
          ? sasUrlMapping[basePartId]
          : sasUrlMapping[part.part_id] ||
            sasUrlMapping[basePartId] ||
            (part.sasKeys as string[] | undefined)?.reduce<string | undefined>(
              (found, key) => found || (key ? sasUrlMapping[key] : undefined),
              undefined,
            );

      if (!sasUrl) return;

      const response = await fetch(sasUrl, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) {
        throw new Error(`Failed to fetch from SAS URL: ${response.statusText}`);
      }
      const stlBlob = await response.blob();
      if (isStale()) return null;

      const file: IFile = {
        id: part.part_id,
        name: `${basePartId}.stl`,
        blob: stlBlob,
      };

      await viewer.loadModels([file]);

      // loadModels gives the object uuid = file.id. Concurrent loads add objects in between, so
      // "last child added" is not reliable; claim the not-yet-claimed object with our uuid instead.
      const loadedObject: any =
        scene.children.find(
          (obj: THREE.Object3D) =>
            obj.type === 'Object3D' &&
            obj.name !== TRAILER_BOX_NAME &&
            obj.uuid === part.part_id &&
            obj.userData[SCENE_GENERATION_KEY] === undefined
        ) ?? null;

      if (loadedObject) {
        loadedObject.userData[SCENE_GENERATION_KEY] = generation;
        if (isStale()) {
          scene.remove(loadedObject);
          return null;
        }
        loadedObject.visible = true;
        loadedObject.matrixAutoUpdate = false;
        const localMatrix = toMatrix4(part.transform_matrix);
        loadedObject.matrix.copy(part.isChild ? toMatrix4(part.parentMatrix).clone().multiply(localMatrix) : localMatrix);

        loadedObject.traverse((child: any) => {
          if (child.isMesh) {
            child.material = new THREE.MeshPhongMaterial({
              color: getIdlePartColor(basePartId),
              specular: new THREE.Color(0x222222),
              shininess: 45,
              transparent: true,
              opacity: 0.9,
              side: THREE.DoubleSide,
            });
          }
        });

        return loadedObject;
      }
      return null;
    } catch (error) {
      console.error('Error loading part:', error);
      return null;
    }
  }, [viewer, currentLoadingPlan?.id, sasUrlMapping]);

  const loadParts = useCallback(async () => {
    const generation = sceneGenerationRef.current;
    const isStale = () => generation !== sceneGenerationRef.current;

    setIsLoadingParts(true);
    const childrenMap: { [palletId: string]: string[] } = {};

    for (const layerKey in layerData) {
      const layer = layerData[layerKey];

      await Promise.all(layer.parts.map(async (part) => {
        await loadSinglePart(part, generation);

        if (part.child_items && part.child_items.length > 0) {
          const childUuids: string[] = [];
          for (const childItem of part.child_items) {
            const loadedChild = await loadSinglePart(childItem, generation);
            if (loadedChild) {
              childUuids.push(loadedChild.uuid);
            }
          }
          childrenMap[part.part_id] = childUuids;
        }
      }));
      await Promise.all(layer.supportObjects.map(async (obj) => loadSinglePart(obj, generation)));

      // A newer scene rebuild owns the viewer now; its own load will publish the state.
      if (isStale()) return;
    }

    const scene = viewer?.getScene();
    setScenePartsMap(prev => {
      const newMap = { ...prev };
      scene?.traverse((child: THREE.Object3D) => {
        if (child.type === 'Object3D' && child.name !== TRAILER_BOX_NAME) {
          newMap[child.uuid] = child;
        }
      });
      return newMap;
    });

    setPalletChildrenMap(childrenMap);
    setIsLoadingParts(false);
  }, [layerData, loadSinglePart, viewer]);

  const getPartFromScene = useCallback((partId: string) => {
    const scene = viewer?.getScene();
    return scene?.children.find((child: any) => child.type === 'Object3D' && child.uuid === partId);
  }, [viewer]);

  // Discards everything the viewer loaded from the previous nesting output so the
  // "load nesting data" effect can rebuild the scene from scratch once a fresh
  // nestingOutput lands in context. `preservePartId` (the part the user was looking at) is
  // handed to that effect via restoreToPartIdRef so the reload lands back on it instead of
  // jumping to the first/last part.
  const resetSceneForReload = useCallback((preservePartId?: string | null) => {
    if (!viewer) return;
    restoreToPartIdRef.current = preservePartId ?? null;
    sceneGenerationRef.current += 1;
    const scene = viewer.getScene();
    const transformControls = getTransformControls();
    const toRemove = scene.children.filter(
      (child: THREE.Object3D) =>
        child !== transformControls && (child.name === 'Trailer Box' || child.type === 'Object3D')
    );
    if (toRemove.length) {
      (viewer as any).removeObjects(...toRemove);
    }

    currentPartRef.current = null;
    hasLoadedPartsRef.current = false;
    hasInitializedVisibilityRef.current = false;
    replacementAcceptedRef.current = false;
    setActivePartId(null);
    setLayerData({});
    setScenePartsMap({});
    setPalletChildrenMap({});
    setCurrentStep(0);
    setCurrentLayer(0);
    setTotalSteps(0);
    setIsLoadingParts(true);
    setIsInitialized(false);
  }, [
    viewer,
    getTransformControls,
    restoreToPartIdRef,
    currentPartRef,
    hasInitializedVisibilityRef,
    replacementAcceptedRef,
    setActivePartId,
    setCurrentStep,
    setCurrentLayer,
    setTotalSteps,
  ]);

  // Rebuilds the scene when something OUTSIDE this component's own edit/lookahead flow changes
  // the plan's nesting result - e.g. Step 4's "Finish Layout" completion nest, which lives in
  // CreateLoadingPlanPage and has no way to know this component needs resetSceneForReload called.
  // Our own flow already calls resetSceneForReload directly (which resets hasLoadedPartsRef
  // before this effect gets to check it) and sets isRenestInFlightRef while its own optimistic
  // update is in flight, so this only fires for genuinely external changes.
  const lastSeenNestingOutputRef = useRef<any>(undefined);
  useEffect(() => {
    const nestingOutput = currentLoadingPlan?.nestingOutput;
    const isFirstObservation = lastSeenNestingOutputRef.current === undefined;
    const changed = nestingOutput !== lastSeenNestingOutputRef.current;
    lastSeenNestingOutputRef.current = nestingOutput;

    if (isFirstObservation || !changed) return;

    if (replacementAcceptedRef.current) {
      replacementAcceptedRef.current = false;
      return;
    }

    if (hasLoadedPartsRef.current && !isRenestInFlightRef.current) {
      resetSceneForReload(currentPartRef.current?.uuid);
    }
  }, [currentLoadingPlan?.nestingOutput, resetSceneForReload, isRenestInFlightRef, currentPartRef, replacementAcceptedRef]);

  // Fetch SAS URLs on component load
  useEffect(() => {
    const fetchSasUrls = async () => {
      if (!currentLoadingPlan?.id) return;

      try {
        const data = await partsService.fetchSignedLinks(currentLoadingPlan.id, 60);

        // Create mapping of partId/mark to sasUrl
        const mapping: SasUrlMapping = {};

        // Map parts by partId, name, and mark
        if (data.parts) {
          data.parts.forEach((item: any) => {
            if (item.partId) {
              mapping[item.partId.toString()] = item.sasUrl;
            }
            if (item.name) {
              mapping[item.name.toString()] = item.sasUrl;
            }
            if (item.mark) {
              mapping[item.mark.toString()] = item.sasUrl;
            }
          });
        }

        // Map support models by name (dunnage, pallet)
        if (data.supportModels) {
          data.supportModels.forEach((item) => {
            if (item.name) {
              mapping[item.name] = item.sasUrl;
            }
          });
        }

        setSasUrlMapping(mapping);
      } catch (error) {
        console.error('Error fetching SAS URLs:', error);
      }
    };

    fetchSasUrls();
  }, [currentLoadingPlan?.id]);

  useEffect(() => {
    if (
      sasUrlMapping &&
      Object.keys(sasUrlMapping).length > 0 &&
      viewer &&
      currentLoadingPlan?.id &&
      layerData &&
      Object.keys(layerData).length > 0
    ) {
      if (hasLoadedPartsRef.current) return;
      hasLoadedPartsRef.current = true;
      loadParts();
    }
  }, [currentLoadingPlan?.id, loadParts, sasUrlMapping, viewer, layerData]);

  // Add trailer box and load nesting data
  useEffect(() => {
    if (!viewer || !currentLoadingPlan?.nestingOutput || isInitialized || !sasUrlMapping || Object.keys(sasUrlMapping).length === 0) {
      return;
    }

    // Reset state
    setCurrentStep(0);
    setCurrentLayer(0);
    // A full scene reload from nesting output clears any prior replacement state,
    // so the visibility effect should manage parts normally again.
    replacementAcceptedRef.current = false;

    // Add transparent trailer box
    const addTrailerBox = () => {
      const dimensions = currentLoadingPlan?.nestingOutput?.truck.dimensions;
      const geometry = new THREE.BoxGeometry(
        dimensions.length_ft,
        dimensions.width_ft,
        dimensions.height_ft
      );

      const material = new THREE.MeshBasicMaterial({
        color: 0x0088ff,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = 'Trailer Box';
      mesh.position.set(dimensions.length_ft / 2, dimensions.width_ft / 2, dimensions.height_ft / 2);

      const edges = new THREE.EdgesGeometry(geometry);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x000000,
        linewidth: 6,
      });
      const lineSegments = new THREE.LineSegments(edges, lineMaterial);
      mesh.add(lineSegments);

      (viewer as any).addObjects([mesh]);
    };

    addTrailerBox();

    // Load nesting output data
    const loadNestingOutput = async () => {
      try {
        const data = { ...currentLoadingPlan.nestingOutput };

        // Count only non-support parts for total steps (excludes dunnages but includes pallets)
        const totalParts = data?.nested_parts?.filter((part: any) => !isDunnagePartId(part.part_id)).length;

        const formatedLayerData = Object.entries(_.groupBy(data?.nested_parts, 'assigned_loading_layer')).reduce((acc, [layer, nestingData]) => {
          const { supportObjects, parts } = nestingData.reduce((layerAcc: any, part: any) => {
            const partObj = {
              part_id: part.part_id,
              part_index: part.part_index,
              orientation_index: part.orientation_index,
              order_number: part.order_number,
              transform_matrix: part.transform_matrix,
              child_items: part.child_items?.map((childItem: any) => ({
                ...childItem,
                isChild: true,
                parentMatrix: part.transform_matrix
              })),
              isChild: false,
            };

            // Only dunnage is a support object now
            if (isDunnagePartId(part.part_id)) {
              layerAcc.supportObjects.push(partObj);
            } else {
              layerAcc.parts.push(partObj);
            }
            return layerAcc;
          }, { supportObjects: [] as LoadedPart[], parts: [] as LoadedPart[] });

          // The API response's array order isn't guaranteed to match placement sequence (e.g. a
          // partial re-nest echoes preplaced parts first, then appends newly-placed ones) - step
          // navigation below walks these arrays by index, so they must be sorted by order_number.
          acc[+layer] = {
            supportObjects: sortNestedParts(supportObjects),
            parts: sortNestedParts(parts),
          }
          return acc;
        }, {} as { [key: number]: LayerData });

        setLayerData(formatedLayerData);

        if (totalParts) {
          setTotalSteps(totalParts);
        }
        setIsInitialized(true);
      } catch (error) {
        console.error('Error loading nesting output:', error);
      }
    };

    loadNestingOutput();
  }, [viewer, currentLoadingPlan.nestingOutput, isInitialized, loadSinglePart, sasUrlMapping, getPartFromScene, setCurrentStep, setCurrentLayer, setTotalSteps, replacementAcceptedRef]);

  return {
    isInitialized,
    layerData,
    setLayerData,
    sasUrlMapping,
    isLoadingParts,
    setIsLoadingParts,
    scenePartsMap,
    setScenePartsMap,
    palletChildrenMap,
    setPalletChildrenMap,
    hasLoadedPartsRef,
    loadSinglePart,
    loadParts,
    getPartFromScene,
    resetSceneForReload,
  };
}
